import { useState, useEffect } from 'react';
import { Check, X, Zap, Users, Building2, Crown, Loader2, Sprout } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PLAN_PRICING } from '../lib/types';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { createCheckoutSession, redirectToCheckout, getStripe } from '../lib/subscription-service';
import type { PlanType } from '../lib/subscription-service';
import BackToHome from './BackToHome';

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [stripeAvailable, setStripeAvailable] = useState(true);

  useEffect(() => {
    const checkStripe = async () => {
      const stripe = await getStripe();
      setStripeAvailable(!!stripe);
    };
    checkStripe();
  }, []);

  const plans = [
    {
      name: 'Free',
      id: 'free',
      icon: Zap,
      description: 'Perfect for trying out Mentra',
      price: PLAN_PRICING.free,
      features: [
        '5 total feedback requests',
        '3 AI analyses per month',
        'Voice & text feedback',
        '100MB storage',
        'Email support'
      ],
      limitations: [
        'No team features',
        'No custom branding',
        'Limited analytics'
      ],
      cta: user ? 'Current Plan' : 'Get Started',
      highlighted: false,
      color: 'gray'
    },
    {
      name: 'Professional',
      id: 'professional',
      icon: Users,
      description: 'For individual professionals',
      price: PLAN_PRICING.professional,
      features: [
        '50 total feedback requests',
        'Unlimited AI analyses',
        'Priority AI processing',
        '5GB storage',
        'PDF export',
        'Custom branding',
        'Advanced analytics',
        'Priority email support'
      ],
      limitations: [],
      cta: 'Upgrade to Pro',
      highlighted: true,
      color: 'blue'
    },
    {
      name: 'Team',
      id: 'team',
      icon: Building2,
      description: 'For teams and organizations',
      price: PLAN_PRICING.team,
      features: [
        '200 total feedback requests',
        'Unlimited AI analyses',
        'Up to 10 team members',
        '20GB storage',
        'Team workspace',
        'Manager dashboard',
        'Bulk feedback requests',
        '360-degree feedback cycles',
        'Advanced analytics',
        'Priority support'
      ],
      limitations: [],
      cta: 'Upgrade to Team',
      highlighted: false,
      color: 'green'
    }
  ];

  const calculateSavings = (plan: typeof plans[0]) => {
    if (billingPeriod === 'annual') {
      const monthlyTotal = plan.price.monthly * 12;
      const annualPrice = plan.price.annual;
      const savings = monthlyTotal - annualPrice;
      const percentage = Math.round((savings / monthlyTotal) * 100);
      return { savings, percentage };
    }
    return { savings: 0, percentage: 0 };
  };

  const handlePlanSelect = async (planId: string) => {
    if (planId === 'free') {
      navigate('/auth');
      return;
    }

    if (!user) {
      navigate('/auth', { state: { returnTo: '/pricing' } });
      return;
    }

    setLoadingPlan(planId);

    try {
      const result = await createCheckoutSession(
        user.id,
        planId as PlanType,
        billingPeriod
      );

      if (!result) {
        showError('Unable to start checkout. Please try again or contact support.');
        return;
      }

      await redirectToCheckout(result.url);
    } catch (error: any) {
      console.error('Checkout error:', error);

      let errorMessage = 'Failed to start checkout process.';

      if (error.message?.includes('Stripe is not configured')) {
        errorMessage = 'Payment system is not configured. Please contact support.';
      } else if (error.message?.includes('Authentication required')) {
        errorMessage = 'Your session has expired. Please sign in again.';
        setTimeout(() => navigate('/auth', { state: { returnTo: '/pricing' } }), 2000);
      } else if (error.message) {
        errorMessage = error.message;
      }

      showError(errorMessage);
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <BackToHome />
        </div>
      </header>

      <main className="container mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-semibold text-[#1F2937] mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-[#6B7280] mb-8">
            Choose the plan that fits your needs. Upgrade or downgrade anytime.
          </p>

          <div className="inline-flex items-center bg-white rounded-[16px] p-1 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-6 py-2 rounded-[12px] font-medium transition ${
                billingPeriod === 'monthly'
                  ? 'bg-[#8CAFA8] text-white'
                  : 'text-[#6B7280] hover:text-[#1F2937]'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('annual')}
              className={`px-6 py-2 rounded-[12px] font-medium transition relative ${
                billingPeriod === 'annual'
                  ? 'bg-[#8CAFA8] text-white'
                  : 'text-[#6B7280] hover:text-[#1F2937]'
              }`}
            >
              Annual
              <span className="absolute -top-2 -right-2 bg-[#8CAFA8] text-white text-xs px-2 py-0.5 rounded-full">
                Save 17%
              </span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-12 max-w-5xl mx-auto">
          {plans.map((plan) => {
            const savings = calculateSavings(plan);
            const Icon = plan.icon;
            const price = billingPeriod === 'monthly' ? plan.price.monthly : plan.price.annual;
            const displayPrice = billingPeriod === 'annual' ? Math.round(price / 12) : price;

            return (
              <div
                key={plan.id}
                className={`bg-white rounded-[24px] shadow-[0_2px_12px_rgba(0,0,0,0.04)] overflow-hidden transition ${
                  plan.highlighted ? 'ring-2 ring-[#8CAFA8]' : ''
                }`}
              >
                {plan.highlighted && (
                  <div className="bg-[#8CAFA8] text-white text-center py-2 text-sm font-medium">
                    Most Popular
                  </div>
                )}

                <div className="p-8">
                  <div className="text-[#8CAFA8] mb-4">
                    <Icon className="w-8 h-8" strokeWidth={1.5} />
                  </div>

                  <h3 className="text-2xl font-semibold text-[#1F2937] mb-2">{plan.name}</h3>
                  <p className="text-[#6B7280] mb-6">{plan.description}</p>

                  <div className="mb-6">
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-semibold text-[#1F2937]">
                        ${displayPrice}
                      </span>
                      <span className="text-[#6B7280]">/month</span>
                    </div>
                    {billingPeriod === 'annual' && price > 0 && (
                      <p className="text-sm text-[#8CAFA8] mt-1">
                        Save ${savings.savings}/year ({savings.percentage}% off)
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => handlePlanSelect(plan.id)}
                    disabled={loadingPlan === plan.id || (plan.id !== 'free' && !stripeAvailable)}
                    className={`w-full py-3 rounded-[16px] font-medium transition flex items-center justify-center gap-2 ${
                      plan.highlighted
                        ? 'bg-[#8CAFA8] text-white hover:bg-[#7A9B94] disabled:opacity-50 shadow-[0_2px_8px_rgba(140,175,168,0.3)]'
                        : 'bg-[#F8F9FA] text-[#1F2937] hover:bg-gray-200 disabled:opacity-50'
                    }`}
                  >
                    {loadingPlan === plan.id && <Loader2 className="w-5 h-5 animate-spin" />}
                    {loadingPlan === plan.id ? 'Processing...' : plan.cta}
                  </button>
                  {plan.id !== 'free' && !stripeAvailable && (
                    <p className="text-xs text-[#6B7280] mt-2 text-center">Payment system unavailable</p>
                  )}

                  <div className="mt-8 space-y-3">
                    {plan.features.map((feature, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-[#8CAFA8] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                        <span className="text-sm text-[#6B7280]">{feature}</span>
                      </div>
                    ))}
                    {plan.limitations.map((limitation, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <X className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                        <span className="text-sm text-gray-400">{limitation}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-[28px] p-8 text-center shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">
            Need a custom solution?
          </h2>
          <p className="text-[#6B7280] mb-6">
            Contact our sales team to discuss enterprise pricing and custom features for your organization.
          </p>
          <a
            href="mailto:sales@getmentra.com"
            className="inline-block bg-[#8CAFA8] text-white px-8 py-3 rounded-[16px] font-medium hover:bg-[#7A9B94] transition shadow-[0_2px_8px_rgba(140,175,168,0.3)]"
          >
            Contact Sales
          </a>
        </div>

        <div className="mt-16 text-center">
          <h2 className="text-3xl font-semibold text-[#1F2937] mb-8">Frequently Asked Questions</h2>
          <div className="grid md:grid-cols-2 gap-6 text-left">
            <div className="bg-white rounded-[24px] p-6 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
              <h3 className="font-medium text-[#1F2937] mb-2">Can I change plans later?</h3>
              <p className="text-[#6B7280]">Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.</p>
            </div>
            <div className="bg-white rounded-[24px] p-6 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
              <h3 className="font-medium text-[#1F2937] mb-2">What payment methods do you accept?</h3>
              <p className="text-[#6B7280]">We accept all major credit cards and debit cards through our secure payment processor Stripe.</p>
            </div>
            <div className="bg-white rounded-[24px] p-6 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
              <h3 className="font-medium text-[#1F2937] mb-2">Is there a free trial?</h3>
              <p className="text-[#6B7280]">Yes! The Free plan is available with no credit card required. Upgrade anytime to unlock more features.</p>
            </div>
            <div className="bg-white rounded-[24px] p-6 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
              <h3 className="font-medium text-[#1F2937] mb-2">What happens if I exceed my limit?</h3>
              <p className="text-[#6B7280]">You will be prompted to upgrade your plan. We will never charge you without your explicit consent.</p>
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t border-gray-100 py-8 mt-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-[#6B7280]">
            <p>&copy; 2025 Mentra. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <a href="/privacy" className="hover:text-[#1F2937] transition">Privacy Policy</a>
              <a href="#" className="hover:text-[#1F2937] transition">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
